import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Shield, Plus, Pencil, Trash2, Search, Filter, ChevronRight, 
  AlertTriangle, Bug, Code, Database, Lock, Eye, Settings, Key, Wrench,
  CheckCircle2, XCircle, Loader2, Copy, Info, Zap, Target,
  Download, Upload, FileJson, FileText, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getCustomRules, createCustomRule, updateCustomRule, deleteCustomRule, toggleCustomRule,
  exportCustomRules, importCustomRules, getRuleTemplates,
  RULE_CATEGORIES, RULE_SEVERITIES, OWASP_MAPPINGS, TEST_LOCATIONS
} from '@/services/litesabrePro';
import RegexBuilder from '@/components/RegexBuilder';

const CATEGORY_ICONS = {
  injection: Database,
  xss: Code,
  authentication: Lock,
  security_headers: Shield,
  information_disclosure: Eye,
  misconfiguration: Settings,
  cryptography: Key,
  custom: Wrench
};

const CustomRulesManager = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const fileInputRef = useRef(null);
  const [showRegexBuilder, setShowRegexBuilder] = useState(false);
  const [regexBuilderIndex, setRegexBuilderIndex] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    severity: 'medium',
    test_locations: ['query_params'],
    payloads: [{ value: '', description: '' }],
    detection_patterns: [''],
    error_signatures: [''],
    owasp_mapping: '',
    cwe_ids: [''],
    remediation: '',
    references: ['']
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await getCustomRules();
      setRules(data.rules || []);
      setStatistics(data.statistics);
    } catch (error) {
      toast.error('Failed to load custom rules');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'custom',
      severity: 'medium',
      test_locations: ['query_params'],
      payloads: [{ value: '', description: '' }],
      detection_patterns: [''],
      error_signatures: [''],
      owasp_mapping: '',
      cwe_ids: [''],
      remediation: '',
      references: ['']
    });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }

    try {
      setSubmitting(true);
      // Clean up empty values
      const cleanData = {
        ...formData,
        payloads: formData.payloads.filter(p => p.value),
        detection_patterns: formData.detection_patterns.filter(p => p),
        error_signatures: formData.error_signatures.filter(p => p),
        cwe_ids: formData.cwe_ids.filter(c => c),
        references: formData.references.filter(r => r)
      };
      
      await createCustomRule(cleanData);
      toast.success('Custom rule created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadRules();
    } catch (error) {
      toast.error('Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (rule) => {
    if (rule.is_predefined) {
      toast.error('Cannot edit predefined rules');
      return;
    }
    setSelectedRule(rule);
    setFormData({
      name: rule.name || '',
      description: rule.description || '',
      category: rule.category || 'custom',
      severity: rule.severity || 'medium',
      test_locations: rule.test_locations || ['query_params'],
      payloads: rule.payloads?.length > 0 ? rule.payloads : [{ value: '', description: '' }],
      detection_patterns: rule.detection_patterns?.length > 0 ? rule.detection_patterns : [''],
      error_signatures: rule.error_signatures?.length > 0 ? rule.error_signatures : [''],
      owasp_mapping: rule.owasp_mapping || '',
      cwe_ids: rule.cwe_ids?.length > 0 ? rule.cwe_ids : [''],
      remediation: rule.remediation || '',
      references: rule.references?.length > 0 ? rule.references : ['']
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }

    try {
      setSubmitting(true);
      const cleanData = {
        ...formData,
        payloads: formData.payloads.filter(p => p.value),
        detection_patterns: formData.detection_patterns.filter(p => p),
        error_signatures: formData.error_signatures.filter(p => p),
        cwe_ids: formData.cwe_ids.filter(c => c),
        references: formData.references.filter(r => r)
      };
      
      await updateCustomRule(selectedRule.id, cleanData);
      toast.success('Rule updated successfully');
      setShowEditDialog(false);
      setSelectedRule(null);
      resetForm();
      loadRules();
    } catch (error) {
      toast.error('Failed to update rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRule) return;
    
    try {
      setSubmitting(true);
      await deleteCustomRule(selectedRule.id);
      toast.success('Rule deleted successfully');
      setShowDeleteDialog(false);
      setSelectedRule(null);
      loadRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      const result = await toggleCustomRule(rule.id);
      toast.success(result.message);
      loadRules();
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDuplicate = (rule) => {
    setFormData({
      name: `${rule.name} (Copy)`,
      description: rule.description || '',
      category: rule.category || 'custom',
      severity: rule.severity || 'medium',
      test_locations: rule.test_locations || ['query_params'],
      payloads: rule.payloads?.length > 0 ? [...rule.payloads] : [{ value: '', description: '' }],
      detection_patterns: rule.detection_patterns?.length > 0 ? [...rule.detection_patterns] : [''],
      error_signatures: rule.error_signatures?.length > 0 ? [...rule.error_signatures] : [''],
      owasp_mapping: rule.owasp_mapping || '',
      cwe_ids: rule.cwe_ids?.length > 0 ? [...rule.cwe_ids] : [''],
      remediation: rule.remediation || '',
      references: rule.references?.length > 0 ? [...rule.references] : ['']
    });
    setShowCreateDialog(true);
  };

  // Import/Export handlers
  const handleExport = async (format = 'json', includePredefined = false) => {
    try {
      setSubmitting(true);
      const blob = await exportCustomRules(format, includePredefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security_rules_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Rules exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export rules');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setSubmitting(true);
      const result = await importCustomRules(file, false);
      toast.success(`Imported ${result.imported} rules, updated ${result.updated}, skipped ${result.skipped}`);
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} errors occurred during import`);
      }
      loadRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import rules');
    } finally {
      setSubmitting(false);
      e.target.value = ''; // Reset file input
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await getRuleTemplates();
      setTemplates(data.templates || []);
      setShowTemplatesDialog(true);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const handleUseTemplate = (template) => {
    setFormData({
      name: template.name.replace(' Template', ''),
      description: template.description || '',
      category: template.category || 'custom',
      severity: template.severity || 'medium',
      test_locations: template.test_locations || ['query_params'],
      payloads: template.payloads?.length > 0 ? [...template.payloads] : [{ value: '', description: '' }],
      detection_patterns: template.detection_patterns?.length > 0 ? [...template.detection_patterns] : [''],
      error_signatures: template.error_signatures?.length > 0 ? [...template.error_signatures] : [''],
      owasp_mapping: template.owasp_mapping || '',
      cwe_ids: template.cwe_ids?.length > 0 ? [...template.cwe_ids] : [''],
      remediation: template.remediation || '',
      references: template.references?.length > 0 ? [...template.references] : ['']
    });
    setShowTemplatesDialog(false);
    setShowCreateDialog(true);
  };

  // Regex Builder handlers
  const openRegexBuilder = (index) => {
    setRegexBuilderIndex(index);
    setShowRegexBuilder(true);
  };

  const handleRegexApply = (pattern) => {
    if (regexBuilderIndex !== null) {
      // Update existing pattern
      updateArrayField('detection_patterns', regexBuilderIndex, pattern);
    } else {
      // Add as new pattern
      const newPatterns = [...formData.detection_patterns.filter(p => p), pattern];
      setFormData({ ...formData, detection_patterns: newPatterns });
    }
    setRegexBuilderIndex(null);
  };

  // Filter rules
  const filteredRules = rules.filter(rule => {
    const matchesSearch = !searchTerm || 
      rule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || rule.category === categoryFilter;
    const matchesSeverity = severityFilter === 'all' || rule.severity === severityFilter;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white',
      info: 'bg-gray-500 text-white'
    };
    return colors[severity] || colors.medium;
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || Wrench;
    return <Icon className="w-4 h-4" />;
  };

  // Array field handlers
  const addArrayField = (field) => {
    if (field === 'payloads') {
      setFormData({ ...formData, payloads: [...formData.payloads, { value: '', description: '' }] });
    } else {
      setFormData({ ...formData, [field]: [...formData[field], ''] });
    }
  };

  const updateArrayField = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray.length > 0 ? newArray : (field === 'payloads' ? [{ value: '', description: '' }] : ['']) });
  };

  const updatePayload = (index, key, value) => {
    const newPayloads = [...formData.payloads];
    newPayloads[index] = { ...newPayloads[index], [key]: value };
    setFormData({ ...formData, payloads: newPayloads });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Rules</p>
                <p className="text-2xl font-bold">{statistics?.total_rules || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Predefined</p>
                <p className="text-2xl font-bold">{statistics?.predefined_rules || 0}</p>
              </div>
              <Lock className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Custom Rules</p>
                <p className="text-2xl font-bold">{statistics?.custom_rules || 0}</p>
              </div>
              <Wrench className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Critical Rules</p>
                <p className="text-2xl font-bold">{statistics?.by_severity?.critical || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Security Rules</CardTitle>
              <CardDescription>Manage predefined and custom security scanning rules</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Hidden file input for import */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json,.yaml,.yml"
                className="hidden"
              />
              <Button variant="outline" size="sm" onClick={loadTemplates}>
                <FileText className="w-4 h-4 mr-2" />
                Templates
              </Button>
              <Button variant="outline" size="sm" onClick={handleImportClick} disabled={submitting}>
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Select onValueChange={(v) => handleExport(v, false)}>
                <SelectTrigger className="w-[120px]">
                  <Download className="w-4 h-4 mr-2" />
                  <span>Export</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">Export JSON</SelectItem>
                  <SelectItem value="yaml">Export YAML</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {RULE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                {RULE_SEVERITIES.map(sev => (
                  <SelectItem key={sev.value} value={sev.value}>{sev.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rules Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead>Rule Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>OWASP</TableHead>
                  <TableHead>Payloads</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      No rules found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Switch
                          checked={rule.enabled !== false}
                          onCheckedChange={() => handleToggle(rule)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(rule.category)}
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[300px]">{rule.description}</p>
                          </div>
                          {rule.is_predefined && (
                            <Badge variant="outline" className="text-xs">Predefined</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{rule.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getSeverityBadge(rule.severity)}>
                          {rule.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{rule.owasp_mapping || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{rule.payloads?.length || 0}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDuplicate(rule)}
                            title="Duplicate"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {!rule.is_predefined && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(rule)}
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => { setSelectedRule(rule); setShowDeleteDialog(true); }}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setSelectedRule(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Custom Rule' : 'Create Custom Rule'}</DialogTitle>
            <DialogDescription>
              Define detection patterns and payloads for your custom security rule
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Rule Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Custom SQL Injection Detection"
                />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this rule detects..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_SEVERITIES.map(sev => (
                      <SelectItem key={sev.value} value={sev.value}>{sev.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Test Locations */}
            <div>
              <Label>Test Locations</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TEST_LOCATIONS.map(loc => (
                  <Button
                    key={loc.value}
                    type="button"
                    variant={formData.test_locations.includes(loc.value) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const newLocs = formData.test_locations.includes(loc.value)
                        ? formData.test_locations.filter(l => l !== loc.value)
                        : [...formData.test_locations, loc.value];
                      setFormData({ ...formData, test_locations: newLocs.length > 0 ? newLocs : ['query_params'] });
                    }}
                  >
                    {loc.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Payloads */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Test Payloads</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => addArrayField('payloads')}>
                  <Plus className="w-4 h-4 mr-1" /> Add Payload
                </Button>
              </div>
              <div className="space-y-2">
                {formData.payloads.map((payload, index) => (
                  <div key={`payload-${index}-${payload.value?.slice(0,10) || ''}`} className="flex gap-2">
                    <Input
                      value={payload.value}
                      onChange={(e) => updatePayload(index, 'value', e.target.value)}
                      placeholder="Payload value"
                      className="flex-1"
                    />
                    <Input
                      value={payload.description}
                      onChange={(e) => updatePayload(index, 'description', e.target.value)}
                      placeholder="Description (optional)"
                      className="flex-1"
                    />
                    {formData.payloads.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayField('payloads', index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Detection Patterns */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Detection Patterns (Regex)</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    onClick={() => { setRegexBuilderIndex(null); setShowRegexBuilder(true); }}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Wand2 className="w-4 h-4 mr-1" /> Regex Builder
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => addArrayField('detection_patterns')}>
                    <Plus className="w-4 h-4 mr-1" /> Add Pattern
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {formData.detection_patterns.map((pattern, index) => (
                  <div key={`pattern-${index}`} className="flex gap-2">
                    <Input
                      value={pattern}
                      onChange={(e) => updateArrayField('detection_patterns', index, e.target.value)}
                      placeholder="e.g., SQL.*syntax|mysql_fetch"
                      className="flex-1 font-mono text-sm"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openRegexBuilder(index)}
                      title="Edit with Regex Builder"
                    >
                      <Wand2 className="w-4 h-4 text-purple-500" />
                    </Button>
                    {formData.detection_patterns.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayField('detection_patterns', index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Use the Regex Builder to create patterns visually or enter raw regex patterns
              </p>
            </div>

            {/* Error Signatures */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Error Signatures</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => addArrayField('error_signatures')}>
                  <Plus className="w-4 h-4 mr-1" /> Add Signature
                </Button>
              </div>
              <div className="space-y-2">
                {formData.error_signatures.map((sig, index) => (
                  <div key={`sig-${index}`} className="flex gap-2">
                    <Input
                      value={sig}
                      onChange={(e) => updateArrayField('error_signatures', index, e.target.value)}
                      placeholder="e.g., You have an error in your SQL syntax"
                      className="flex-1"
                    />
                    {formData.error_signatures.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayField('error_signatures', index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mappings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>OWASP Mapping</Label>
                <Select value={formData.owasp_mapping || "none"} onValueChange={(v) => setFormData({ ...formData, owasp_mapping: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select OWASP category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {OWASP_MAPPINGS.map(owasp => (
                      <SelectItem key={owasp.value} value={owasp.value}>{owasp.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CWE IDs</Label>
                <Input
                  value={formData.cwe_ids.join(', ')}
                  onChange={(e) => setFormData({ ...formData, cwe_ids: e.target.value.split(',').map(s => s.trim()) })}
                  placeholder="e.g., CWE-89, CWE-79"
                />
              </div>
            </div>

            {/* Remediation */}
            <div>
              <Label>Remediation Guidance</Label>
              <Textarea
                value={formData.remediation}
                onChange={(e) => setFormData({ ...formData, remediation: e.target.value })}
                placeholder="How to fix vulnerabilities detected by this rule..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
              setSelectedRule(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={showEditDialog ? handleUpdate : handleCreate}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {showEditDialog ? 'Save Changes' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Custom Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedRule?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={showTemplatesDialog} onOpenChange={setShowTemplatesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Rule Templates</DialogTitle>
            <DialogDescription>
              Start with a pre-built template for common vulnerability types
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {templates.map((template) => (
              <Card key={template.name} className="cursor-pointer hover:border-blue-500 transition-colors" onClick={() => handleUseTemplate(template)}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-medium">{template.name}</h4>
                      <p className="text-sm text-gray-500">{template.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{template.category}</Badge>
                        <Badge className={getSeverityBadge(template.severity)}>{template.severity}</Badge>
                        <span className="text-xs text-gray-400">{template.payloads?.length || 0} payloads</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Use Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <p className="text-center text-gray-500 py-4">No templates available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Regex Builder Dialog */}
      <RegexBuilder
        isOpen={showRegexBuilder}
        onClose={() => { setShowRegexBuilder(false); setRegexBuilderIndex(null); }}
        onApply={handleRegexApply}
        initialPattern={regexBuilderIndex !== null ? formData.detection_patterns[regexBuilderIndex] : ''}
      />
    </div>
  );
};

export default CustomRulesManager;
